import React, { useEffect } from 'react';
import axios from 'axios';
import { Button, DetailsPanel } from '@quyva/shared-ui';
import { handleHtmlStringToElement } from '@quyva/common';
import { faClipboardList, faXmark } from '@fortawesome/free-solid-svg-icons';
import { getUrl } from '../../utils/getUrl';
import { Config } from '../../config/envConfig';

export const TaskDetailsPane = ({
    selectedTask,
    selectedTaskFiles,
    setSelectedTaskId,
    renderStatusPill,
    // Set when this task is parked waiting for the user to fill in a form. The task tab is
    // where that happens: the run is waiting on the person who started it.
    waitingFormConfig,
    onFillWaitingForm,
    onCancelWaitingTask,
    isResolvingSuspension,
    isExtractionTaskType,
    extractedFilesByTask,
    setExtractedFilesByTask,
    handleDownloadExtractedFile,
    handleDownloadRepoFile,
}) => {
    const selectedTaskFilesKey = selectedTask
        ? selectedTask.mergeKey || selectedTask.id
        : null;

    useEffect(() => {
        if (!selectedTask) return;
        if (!isExtractionTaskType(selectedTask.taskType)) return;
        if (selectedTask.status !== 'Success') return;

        const taskKey = selectedTask.mergeKey || selectedTask.id;
        if (taskKey && extractedFilesByTask[taskKey]) return;

        const fetchExtractedFiles = async () => {
            try {
                const email = selectedTask.userId;
                const folderName = selectedTask.repoOrAgentName;
                const templateName = selectedTask.templateName;

                if (!email || !folderName || !templateName) return;

                const response = await axios.get(
                    getUrl(
                        `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/redownloadfile_excell?email=${email}&foldername=${folderName}&templatename=${templateName}`
                    )
                );

                const files = Array.isArray(response.data?.files)
                    ? response.data.files
                    : [];

                if (!files.length) return;
                if (!taskKey) return;

                setExtractedFilesByTask((prev) => ({
                    ...prev,
                    [taskKey]: files,
                }));
            } catch (error) {
                console.error('Failed to fetch extracted files for task', error);
            }
        };

        fetchExtractedFiles();
    }, [
        selectedTask,
        extractedFilesByTask,
        setExtractedFilesByTask,
        isExtractionTaskType,
        selectedTaskFilesKey,
    ]);

    if (!selectedTask) return null;

    return (
        <DetailsPanel
            title={selectedTask.taskType}
            subtitle={`(${selectedTask.repoOrAgentName})`}
            onClose={() => setSelectedTaskId(null)}
            className='rounded-r-xl rounded-l-none overflow-hidden border border-backgrounds-border'
            headerClassName='!bg-[#f1f5f9]'
            titleClassName='text-sm font-semibold text-texts-secondary'
            subtitleClassName='mt-0.5 text-xs text-texts-secondary300'
            contentClassName='overflow-x-hidden'
            statusBar={
                <div className='px-4 py-3 border-b border-backgrounds-border flex flex-wrap gap-3 text-xs text-texts-secondary300 bg-backgrounds-primary100/60'>
                    {renderStatusPill(selectedTask.status)}
                </div>
            }
        >
            {waitingFormConfig && (
                <div className='bg-backgrounds-white rounded-lg border border-landing-orange-500 px-4 py-3'>
                    <div className='text-xs font-semibold text-texts-secondary300 mb-1'>
                        Waiting for your details
                    </div>
                    <div className='text-sm text-texts-secondary mb-3 break-words'>
                        {selectedTask.waitingMessage ||
                            'This task needs a few details from you before it can continue.'}
                    </div>
                    <div className='flex flex-wrap items-center gap-2'>
                        <Button
                            type='success'
                            text='Fill in details'
                            icon={faClipboardList}
                            onClick={onFillWaitingForm}
                            disabled={isResolvingSuspension}
                            hoverText='Fill in the details this task is waiting for'
                        />
                        <Button
                            type='danger'
                            text='Cancel task'
                            icon={faXmark}
                            onClick={onCancelWaitingTask}
                            disabled={isResolvingSuspension}
                            hoverText='End this task without providing the details'
                        />
                    </div>
                </div>
            )}

            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                <div className='bg-backgrounds-white rounded-lg border border-backgrounds-border px-4 py-3'>
                    <div className='text-xs font-semibold text-texts-secondary300 mb-1'>
                        Start time
                    </div>
                    <div className='text-sm text-texts-secondary'>
                        {selectedTask.creationTime || '-'}
                    </div>
                </div>

                <div className='bg-backgrounds-white rounded-lg border border-backgrounds-border px-4 py-3'>
                    <div className='text-xs font-semibold text-texts-secondary300 mb-1'>
                        Completion time
                    </div>
                    <div className='text-sm text-texts-secondary'>
                        {selectedTask.status === 'Processing'
                            ? '-'
                            : selectedTask.completionTime || '-'}
                    </div>
                </div>
            </div>

            <div className='bg-backgrounds-white rounded-lg border border-backgrounds-border px-4 py-3'>
                <div className='text-xs font-semibold text-texts-secondary300 mb-1'>
                    {selectedTask.status === 'Processing'
                        ? 'Current Update'
                        : 'Final Update'}
                    {typeof selectedTask.percentageCompletion === 'number' && (
                        <span className='ml-2 font-normal text-texts-secondary300'>
                            ({selectedTask.status} {selectedTask.percentageCompletion}%
                            complete)
                        </span>
                    )}
                </div>
                <div className='space-y-1 text-sm text-texts-secondary'>
                    {(selectedTask.lastUpdateTime || selectedTask.timestamp) && (
                        <div className='text-xs text-texts-secondary300'>
                            Last update:{' '}
                            {selectedTask.lastUpdateTime || selectedTask.timestamp}
                        </div>
                    )}

                    {(() => {
                        const raw =
                            (typeof selectedTask.message === 'string'
                                ? selectedTask.message
                                : '') || '';
                        const parts = raw
                            .split('\n\n')
                            .map((p) => p.trim())
                            .filter(Boolean);

                        if (parts.length <= 1) {
                            return (
                                <div className='break-words text-xs text-texts-secondary300'>
                                    {parts[0] || 'No update message available.'}
                                </div>
                            );
                        }

                        return (
                            <ul className='list-disc list-inside space-y-0.5 text-xs text-texts-secondary300'>
                                {parts.map((p, idx) => (
                                    <li
                                        key={idx}
                                        className='break-words text-texts-secondary300'
                                    >
                                        {p}
                                    </li>
                                ))}
                            </ul>
                        );
                    })()}
                </div>
            </div>

            {(selectedTask.parsedOutput ||
                (selectedTask.outputFiles &&
                    selectedTask.outputFiles.length > 0) ||
                (selectedTask.addedFileNames &&
                    selectedTask.addedFileNames.length > 0) ||
                (isExtractionTaskType(selectedTask.taskType) &&
                    selectedTaskFiles &&
                    selectedTaskFiles.length > 0)) && (
                <div className='bg-backgrounds-white rounded-lg border border-backgrounds-border px-4 py-3'>
                    <div className='text-xs font-semibold text-texts-secondary300 mb-1'>
                        Output
                    </div>

                    {isExtractionTaskType(selectedTask.taskType) &&
                        selectedTaskFiles &&
                        selectedTaskFiles.length > 0 && (
                            <div className='mb-2 text-sm text-texts-secondary'>
                                <span className='font-semibold'>
                                    Extracted data:
                                </span>
                                <div className='mt-1 space-y-1'>
                                    {selectedTaskFiles.map((fileName) => (
                                        <button
                                            key={fileName}
                                            type='button'
                                            className='text-icons-primary hover:text-icons-primary/80 underline break-words text-left'
                                            onClick={() =>
                                                handleDownloadExtractedFile(
                                                    selectedTask,
                                                    fileName
                                                )
                                            }
                                        >
                                            {fileName}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                    {selectedTask.addedFileNames &&
                        selectedTask.addedFileNames.length > 0 && (
                            <div className='mb-2 text-sm text-texts-secondary'>
                                <span className='font-semibold'>
                                    Added files:
                                </span>
                                <div className='mt-1 space-y-1'>
                                    {selectedTask.addedFileNames.map((name) => (
                                        <button
                                            key={name}
                                            type='button'
                                            className='text-icons-primary hover:text-icons-primary/80 underline break-words text-left'
                                            onClick={() =>
                                                handleDownloadRepoFile(
                                                    selectedTask,
                                                    name
                                                )
                                            }
                                        >
                                            {name}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                    {selectedTask.parsedOutput?.response && (
                        <div className='mb-2 text-sm text-texts-secondary'>
                            <span className='font-semibold'>Output text: </span>
                            <span className='break-words'>
                                {handleHtmlStringToElement(
                                    selectedTask.parsedOutput.response
                                )}
                            </span>
                        </div>
                    )}

                    {selectedTask.parsedOutput && (
                        <div className='space-y-1 text-xs text-texts-secondary300'>
                            {Object.entries(selectedTask.parsedOutput)
                                .filter(([key]) => key !== 'response')
                                .map(([key, value]) => (
                                    <div key={key}>
                                        <span className='font-semibold'>
                                            {key}
                                        </span>
                                        {': '}
                                        <span className='text-texts-secondary break-words'>
                                            {typeof value === 'string'
                                                ? value
                                                : JSON.stringify(value)}
                                        </span>
                                    </div>
                                ))}
                        </div>
                    )}

                    {selectedTask.outputFiles &&
                        selectedTask.outputFiles.length > 0 && (
                            <div className='mt-3'>
                                <div className='text-xs font-semibold text-texts-secondary300 mb-1'>
                                    Output files
                                </div>
                                <div className='space-y-1'>
                                    {selectedTask.outputFiles.map((file) => (
                                        <button
                                            key={file}
                                            type='button'
                                            className='text-icons-primary hover:text-icons-primary/80 underline break-words text-left'
                                            onClick={() =>
                                                handleDownloadRepoFile(
                                                    selectedTask,
                                                    file
                                                )
                                            }
                                        >
                                            {String(file).split(/[\\/]/).pop()}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                </div>
            )}
        </DetailsPanel>
    );
};
